<template>
  <v-card data-testid="user-data-page-settings">
    <v-card-text>
      <div class="custom-data-wrapper">
        <div class="subtitle1 mb-4">
          {{ $t("userDataGovernanceSettings.monitoringTab.securityAndBusinessData") }}
        </div>
        <v-checkbox
          v-model="localValue.customData.monitorPasswords"
          :disabled="actionNotAllowed"
          data-testid="custom-data-monitor-passwords-checkbox"
          :ripple="false"
          :label="$t('userDataGovernanceSettings.monitoringTab.passwords')"
          class="mt-3 mb-1"
        />
        <v-checkbox
          v-model="localValue.customData.monitorCertificates"
          :disabled="actionNotAllowed"
          data-testid="custom-data-monitor-certificates-checkbox"
          :ripple="false"
          :label="$t('userDataGovernanceSettings.monitoringTab.certificates')"
          class="mt-3 mb-1"
        />
        <v-checkbox
          v-model="localValue.customData.monitorSourceCodes"
          :disabled="actionNotAllowed"
          data-testid="custom-data-monitor-source-codes-checkbox"
          :ripple="false"
          :label="$t('userDataGovernanceSettings.monitoringTab.sourceCode')"
          class="mt-3 mb-1"
        />
        <v-checkbox
          v-model="localValue.customData.monitorSensitiveKeywords"
          :disabled="actionNotAllowed"
          data-testid="custom-data-monitor-sensitive-keywords-checkbox"
          :ripple="false"
          :label="$t('userDataGovernanceSettings.monitoringTab.dataObjectsWithSpecificKeywords')"
          class="mt-3 mb-1"
        />
        <v-combobox
          v-model="localValue.customData.sensitiveKeywords"
          :disabled="actionNotAllowed || !localValue.customData.monitorSensitiveKeywords"
          data-testid="custom-data-monitor-keywords-combobox"
          variant="outlined"
          multiple
          item-color="red"
          class="ml-7"
          :rules="sensitiveKeywordsRule"
          :placeholder="$t('userDataGovernanceSettings.monitoringTab.specificKeywordsPlaceholder')"
          :aria-label="$t('userDataGovernanceSettings.monitoringTab.specificKeywordsPlaceholder')"
          @update:search="onKeywordInput('sensitiveKeywords')"
        >
          <template v-slot:chip="{ item, props }">
            <v-chip
              v-bind="props"
              :disabled="actionNotAllowed"
              :data-testid="`chip-${item}`"
              close-icon="$closeCircle"
              :closable="true"
              variant="flat"
              color="indigo-faint"
            >
              <div class="d-flex align-center justify-space-between">
                <span class="ml-1 mr-2 chip-content__text">{{ item.value }}</span>
              </div>
            </v-chip>
          </template>
          <template #item="data">
            <v-list-item>
              <span class="ml-1">{{ data.item }}</span>
            </v-list-item>
          </template>
        </v-combobox>
        <v-checkbox
          v-model="localValue.customData.monitorSensitiveFileTypes"
          :disabled="actionNotAllowed"
          data-testid="custom-data-monitor-sensitive-file-types-checkbox"
          :ripple="false"
          :label="$t('userDataGovernanceSettings.monitoringTab.specificFileTypesTitle')"
          class="mt-3 mb-1"
        />
        <v-combobox
          v-model="localValue.customData.sensitiveFileTypes"
          :disabled="actionNotAllowed || !localValue.customData.monitorSensitiveFileTypes"
          data-testid="custom-data-monitor-file-types-combobox"
          variant="outlined"
          multiple
          class="ml-7"
          :placeholder="$t('userDataGovernanceSettings.monitoringTab.specificFileTypesPlaceholder')"
          :aria-label="$t('userDataGovernanceSettings.monitoringTab.specificFileTypesPlaceholder')"
          @update:search="onKeywordInput('sensitiveFileTypes')"
        >
          <template v-slot:chip="{ item, props }">
            <v-chip
              v-bind="props"
              :disabled="actionNotAllowed"
              :data-testid="`chip-${item}`"
              close-icon="$closeCircle"
              :closable="true"
              variant="flat"
              color="indigo-faint"
            >
              <div class="d-flex align-center justify-space-between">
                <span class="ml-1 mr-2 chip-content__text">{{ item.value }}</span>
              </div>
            </v-chip>
          </template>
          <template #item="data">
            <v-list-item>
              <span class="ml-1">{{ data.item }}</span>
            </v-list-item>
          </template>
        </v-combobox>
      </div>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import { hasSeparator } from "@coronet/cui";
import type { UserDataGovernanceSettings } from "@/_store/user-data-governance/user-data-governance.module";

export default defineComponent({
  props: {
    modelValue: {
      type: Object as PropType<UserDataGovernanceSettings>,
      required: true,
    },
    actionNotAllowed: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const i18n = useI18n();

    const localValue = computed({
      get: () => props.modelValue,
      set: (value) => emit("update:modelValue", value),
    });

    const sensitiveKeywordsRule = [
      (keywords: string[]) => {
        if (keywords?.length > 200) {
          return i18n.t("validations.maximumAllowedKeywords");
        }
        return true;
      },
    ];

    const onKeywordInput = (arrayKey: "sensitiveKeywords" | "sensitiveFileTypes") => {
      const previousValues = localValue.value!.customData[arrayKey];
      const lastKeyword = previousValues?.at(-1);
      const hasCommaSeparator = hasSeparator(lastKeyword!);

      if (hasCommaSeparator) {
        const split = lastKeyword!.split(",");
        localValue.value!.customData[arrayKey] = split.map((item) => item.toLowerCase().trim());
      }
    };

    return {
      localValue,
      sensitiveKeywordsRule,
      onKeywordInput,
    };
  },
});
</script>

<style scoped lang="scss">
:deep(*) {
  .v-field__input {
    opacity: 1;
  }

  .v-field .v-chip {
    --v-chip-height: 32px;
  }
}
.chip-content__text {
  max-width: 400px;
  text-overflow: ellipsis;
  overflow: hidden;
}
</style>
